import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import {
  FilterList,
  Assessment,
  Category,
  Person,
  Inventory2
} from '@mui/icons-material';
import statsService from '../services/statsService';
import './Reports.css';

// Helper para obtener fecha local en formato YYYY-MM-DD
const getLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  
  // Estados para datos
  const [productStats, setProductStats] = useState(null);
  const [categoryStats, setCategoryStats] = useState(null);
  const [sellerStats, setSellerStats] = useState(null);
  
  // Filtros de fecha (mes actual por defecto)
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      desde: getLocalDateString(firstDayOfMonth),
      hasta: getLocalDateString(today)
    };
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Cargar estadísticas por producto
      const productResult = await statsService.getPorProducto({
        desde: dateRange.desde,
        hasta: dateRange.hasta,
        limit: 50
      });
      if (productResult.success) {
        setProductStats(productResult.data);
      }

      // Cargar estadísticas por categoría
      const categoryResult = await statsService.getPorCategoria({
        desde: dateRange.desde,
        hasta: dateRange.hasta
      });
      if (categoryResult.success) {
        setCategoryStats(categoryResult.data);
      }

      // Cargar estadísticas por vendedor
      const sellerResult = await statsService.getPorVendedor({
        desde: dateRange.desde,
        hasta: dateRange.hasta
      });
      if (sellerResult.success) {
        setSellerStats(sellerResult.data);
      }
    } catch (err) {
      setError('Error al cargar los reportes');
      console.error(err);
    }

    setLoading(false);
  };

  const handleFilterChange = (field, value) => {
    setDateRange({ ...dateRange, [field]: value });
  };

  const handleApplyFilters = () => {
    if (!dateRange.desde || !dateRange.hasta) {
      setError('Seleccioná ambas fechas para generar el reporte');
      return;
    }
    if (dateRange.desde > dateRange.hasta) {
      setError('La fecha desde no puede ser posterior a la fecha hasta');
      return;
    }
    loadData();
  };

  const reportSummary = (productStats?.productos || []).reduce((summary, product) => ({
    sales: summary.sales + parseFloat(product.ventaTotal || 0),
    costs: summary.costs + parseFloat(product.costoTotal || 0),
    profit: summary.profit + parseFloat(product.ganancia || 0)
  }), { sales: 0, costs: 0, profit: 0 });
  const reportMargin = reportSummary.sales > 0 ? (reportSummary.profit / reportSummary.sales) * 100 : 0;

  const renderProductsReport = () => {
    if (!productStats || !productStats.productos) return null;

    return (
      <Card className="reports-table-card">
        <CardContent>
          <Typography variant="h6" className="mb-md font-semibold">
            <Box className="d-flex align-center gap-sm">
              <Inventory2 />
              Ganancias por Producto
            </Box>
          </Typography>
          <Box className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Código</th>
                  <th>Categoría</th>
                  <th className="text-center">Cantidad</th>
                  <th className="text-right">Ventas</th>
                  <th className="text-right">Costos</th>
                  <th className="text-right">Ganancia</th>
                  <th className="text-center">Margen %</th>
                </tr>
              </thead>
              <tbody>
                {productStats.productos.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      No hay datos para mostrar en este período
                    </td>
                  </tr>
                ) : (
                  productStats.productos.map((product, index) => (
                    <tr key={product.productoId || `product-${index}`}>
                      <td className="font-semibold">{product.nombre}</td>
                      <td className="font-mono">{product.codigo}</td>
                      <td>{product.categoria || '-'}</td>
                      <td className="text-center">{product.cantidadVendida}</td>
                      <td className="text-right">
                        ${parseFloat(product.ventaTotal).toLocaleString('es-AR')}
                      </td>
                      <td className="text-right text-cost">
                        ${parseFloat(product.costoTotal).toLocaleString('es-AR')}
                      </td>
                      <td className="text-right font-semibold text-profit">
                        ${parseFloat(product.ganancia).toLocaleString('es-AR')}
                      </td>
                      <td className="text-center">
                        <Chip
                          label={`${parseFloat(product.margenGanancia).toFixed(1)}%`}
                          size="small"
                          color={product.margenGanancia >= 40 ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderCategoriesReport = () => {
    if (!categoryStats || !categoryStats.categorias) return null;

    return (
      <Card className="reports-table-card">
        <CardContent>
          <Typography variant="h6" className="mb-md font-semibold">
            <Box className="d-flex align-center gap-sm">
              <Category />
              Ganancias por Categoría
            </Box>
          </Typography>
          <Box className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Categoría</th>
                  <th className="text-center">Productos</th>
                  <th className="text-center">Cantidad Vendida</th>
                  <th className="text-right">Ventas</th>
                  <th className="text-right">Costos</th>
                  <th className="text-right">Ganancia</th>
                  <th className="text-center">Margen %</th>
                </tr>
              </thead>
              <tbody>
                {categoryStats.categorias.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No hay datos para mostrar en este período
                    </td>
                  </tr>
                ) : (
                  categoryStats.categorias.map((category, index) => (
                    <tr key={category.categoriaId || `category-${index}`}>
                      <td className="font-semibold">{category.nombre}</td>
                      <td className="text-center">{category.cantidadProductos}</td>
                      <td className="text-center">{category.cantidadVendida}</td>
                      <td className="text-right">
                        ${parseFloat(category.ventaTotal).toLocaleString('es-AR')}
                      </td>
                      <td className="text-right text-cost">
                        ${parseFloat(category.costoTotal).toLocaleString('es-AR')}
                      </td>
                      <td className="text-right font-semibold text-profit">
                        ${parseFloat(category.ganancia).toLocaleString('es-AR')}
                      </td>
                      <td className="text-center">
                        <Chip
                          label={`${parseFloat(category.margenGanancia).toFixed(1)}%`}
                          size="small"
                          color={category.margenGanancia >= 40 ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderSellersReport = () => {
    if (!sellerStats || !sellerStats.vendedores) return null;

    return (
      <Card className="reports-table-card">
        <CardContent>
          <Typography variant="h6" className="mb-md font-semibold">
            <Box className="d-flex align-center gap-sm">
              <Person />
              Estadísticas por Vendedor
            </Box>
          </Typography>
          <Box className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Vendedor</th>
                  <th>Usuario</th>
                  <th className="text-center">Ventas</th>
                  <th className="text-right">Total Vendido</th>
                  <th className="text-right">Costos</th>
                  <th className="text-right">Ganancia</th>
                  <th className="text-center">Margen %</th>
                  <th className="text-right">Ticket Prom.</th>
                </tr>
              </thead>
              <tbody>
                {sellerStats.vendedores.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      No hay datos para mostrar en este período
                    </td>
                  </tr>
                ) : (
                  sellerStats.vendedores.map((seller, index) => (
                    <tr key={seller.vendedorId || `seller-${index}`}>
                      <td className="font-semibold">{seller.nombre}</td>
                      <td className="font-mono">{seller.username}</td>
                      <td className="text-center">{seller.cantidadVentas}</td>
                      <td className="text-right">
                        ${parseFloat(seller.ventaTotal).toLocaleString('es-AR')}
                      </td>
                      <td className="text-right text-cost">
                        ${parseFloat(seller.costoTotal).toLocaleString('es-AR')}
                      </td>
                      <td className="text-right font-semibold text-profit">
                        ${parseFloat(seller.ganancia).toLocaleString('es-AR')}
                      </td>
                      <td className="text-center">
                        <Chip
                          label={`${parseFloat(seller.margenGanancia).toFixed(1)}%`}
                          size="small"
                          color={seller.margenGanancia >= 40 ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </td>
                      <td className="text-right">
                        ${parseFloat(seller.ticketPromedio).toLocaleString('es-AR')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box className="reports-page admin-page">
      <Box className="reports-heading">
        <Box className="reports-title-icon">
          <Assessment />
        </Box>
        <Box>
          <Typography className="reports-kicker">RENTABILIDAD</Typography>
          <Typography variant="h4">Reportes de ganancias</Typography>
          <Typography className="reports-subtitle">
            Analizá ventas, costos y márgenes por período.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} className="mb-md">
          {error}
        </Alert>
      )}

      {/* Filtros de fecha */}
      <Card className="reports-filter-card">
        <CardContent>
          <Box className="reports-card-heading">
            <Typography className="reports-card-kicker">PERÍODO</Typography>
            <Typography variant="h6">Rango del informe</Typography>
          </Box>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Fecha Desde"
                value={dateRange.desde}
                onChange={(e) => handleFilterChange('desde', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Fecha Hasta"
                value={dateRange.hasta}
                onChange={(e) => handleFilterChange('hasta', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} /> : <FilterList />}
                onClick={handleApplyFilters}
                disabled={loading}
              >
                {loading ? 'Cargando...' : 'Aplicar Filtros'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2} className="reports-summary">
        <Grid item xs={12} sm={6} lg={3}>
          <Card>
            <CardContent>
              <Typography>VENTAS</Typography>
              <strong>${reportSummary.sales.toLocaleString('es-AR')}</strong>
              <span>Facturación del período</span>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Card>
            <CardContent>
              <Typography>COSTOS</Typography>
              <strong>${reportSummary.costs.toLocaleString('es-AR')}</strong>
              <span>Costo total de mercadería</span>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Card className="reports-profit-card">
            <CardContent>
              <Typography>GANANCIA</Typography>
              <strong>${reportSummary.profit.toLocaleString('es-AR')}</strong>
              <span>Resultado bruto del período</span>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Card>
            <CardContent>
              <Typography>MARGEN</Typography>
              <strong>{reportMargin.toFixed(1)}%</strong>
              <span>Rentabilidad sobre ventas</span>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs de reportes */}
      <Card className="reports-tabs-card">
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab key="tab-0" icon={<Inventory2 />} label="Por Producto" />
          <Tab key="tab-1" icon={<Category />} label="Por Categoría" />
          <Tab key="tab-2" icon={<Person />} label="Por Vendedor" />
        </Tabs>
      </Card>

      {/* Contenido según tab */}
      {loading ? (
        <Box className="d-flex justify-center align-center py-lg">
          <CircularProgress size={60} />
        </Box>
      ) : (
        <Box>
          {currentTab === 0 && renderProductsReport()}
          {currentTab === 1 && renderCategoriesReport()}
          {currentTab === 2 && renderSellersReport()}
        </Box>
      )}
    </Box>
  );
};

export default Reports;

